/**
 * #40 · Detalle de una orden de etiquetas: ficha, generación, archivo del
 * impresor y los rollos que produjo.
 *
 * Mientras la generación corre, el detalle se repregunta cada segundo: es de
 * dónde sale el "progreso en vivo" del criterio de aceptación (ver la cabecera
 * de `GenerationPanel.tsx` para por qué no hay un porcentaje).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useApiQuery, useKeysetQuery } from '@/api/hooks';
import { labelOrderKeys, rollKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import { BACKOFFICE_SLUG } from '@/layout/nav';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState, QueryBoundary } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { Dialog } from '../products/ui/Dialog';
import { DetailList, DetailRow } from '../products/ui/fields';
import {
  ORDER_STATUS_LABEL_KEYS,
  ORDER_STATUS_TONES,
  ROLL_STATE_LABEL_KEYS,
  ROLL_STATE_TONES,
  isOrderStatus,
  isRollState,
  serialRange,
} from './custody';
import { GenerationPanel } from './GenerationPanel';
import { LabelsTabs } from './LabelsTabs';
import { OrderKeyPanel } from './OrderKeyPanel';
import { PrintFilePanel } from './PrintFilePanel';
import { VerifyPanel } from './VerifyPanel';

type LabelOrder = Schemas['LabelOrderOut'];
type Roll = Schemas['RollOut'];

const ROLLS_PAGE = 50;
/** Cadencia del sondeo mientras la orden está generando. */
const POLL_MS = 1000;

export function LabelOrderDetailPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can, claims } = useAuth();
  // Cancelar, generar, el archivo del impresor y su clave son del equipo de
  // XYVONA (#143): la API responde 403 `backoffice_only` a cualquier marca,
  // así que mostrárselos sería ofrecer botones que sólo pueden fallar. Una
  // marca llega acá por el enlace viejo de una orden anterior a #142, y lo
  // que le sirve es leerla. La clave exige además el permiso
  // `print_file:reveal_key`, y eso lo decide la API.
  const opera = can('admin') && claims?.tenant === BACKOFFICE_SLUG;
  const api = useApi();
  const queryClient = useQueryClient();
  const params = useParams<{ orderId: string }>();
  const orderId = Number(params.orderId);
  const valid = Number.isInteger(orderId) && orderId > 0;

  const [generating, setGenerating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Sondeo. Se enciende mientras la mutación está en vuelo **o** mientras el
  // servidor diga `generating` — lo segundo cubre el caso de que la generación
  // la haya disparado otra pestaña o el navegador se haya recargado a mitad.
  // Un sondeo permanente sobre una orden terminada es tráfico que no cambia
  // nada, así que se apaga solo.
  const [polling, setPolling] = useState(false);

  const order = useApiQuery<LabelOrder>(
    labelOrderKeys.detail(orderId),
    `/api/v1/label-orders/${String(orderId)}`,
    undefined,
    { enabled: valid, ...(polling ? { refetchInterval: POLL_MS } : {}) },
  );

  // Ajuste de estado **durante el render**, no en un efecto: es el patrón que
  // recomienda React para "derivar estado de props/datos que cambiaron", y
  // evita el render de más que costaría hacerlo en un `useEffect`.
  const shouldPoll = generating || order.data?.status === 'generating';
  if (shouldPoll !== polling) setPolling(shouldPoll);

  const rolls = useKeysetQuery<Roll>(
    rollKeys.list({ limit: ROLLS_PAGE, state: null, orderId }),
    '/api/v1/rolls',
    { order_id: orderId },
    { limit: ROLLS_PAGE, enabled: valid },
  );

  const cancel = useMutation<LabelOrder, unknown>({
    mutationFn: () => api.post<LabelOrder>(`/api/v1/label-orders/${String(orderId)}/cancel`),
    onSuccess: () => {
      setCancelling(false);
      void queryClient.invalidateQueries({ queryKey: labelOrderKeys.all });
    },
  });

  const onGeneratingChange = useCallback((value: boolean) => {
    setGenerating(value);
  }, []);

  if (!valid) {
    return (
      <div className="mx-auto max-w-4xl">
        <Card>
          <CardBody>
            <ErrorState error={new Error(t('labels.order.badId'))} />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <LabelsTabs active="orders" />
      <Link to="/labels" className="inline-block text-sm text-accent underline underline-offset-2">
        {t('labels.orders.back')}
      </Link>

      <QueryBoundary query={order}>
        {(data) => (
          <>
            <Card>
              <CardHeader
                title={data.order_code}
                description={data.supplier}
                actions={
                  <>
                    <Badge
                      tone={isOrderStatus(data.status) ? ORDER_STATUS_TONES[data.status] : 'neutral'}
                    >
                      {isOrderStatus(data.status)
                        ? t(ORDER_STATUS_LABEL_KEYS[data.status])
                        : data.status}
                    </Badge>
                    {opera && data.status === 'draft' && (
                      <Button
                        size="sm"
                        variant="critical"
                        onClick={() => {
                          cancel.reset();
                          setCancelling(true);
                        }}
                      >
                        {t('labels.order.cancel')}
                      </Button>
                    )}
                  </>
                }
              />
              <CardBody>
                <DetailList>
                  <DetailRow
                    label={t('labels.orders.column.quantity')}
                    value={<span className="tabular-nums">{fmt.number(data.quantity)}</span>}
                  />
                  <DetailRow
                    label={t('labels.order.field.unitsPerRoll')}
                    value={<span className="tabular-nums">{fmt.number(data.units_per_roll)}</span>}
                  />
                  <DetailRow
                    label={t('labels.orders.column.rolls')}
                    value={<span className="tabular-nums">{fmt.number(data.roll_count)}</span>}
                  />
                  <DetailRow
                    label={t('labels.orders.column.serials')}
                    value={
                      <code className="font-mono text-xs">
                        {serialRange(data.serial_from, data.serial_to, (value) =>
                          fmt.number(value),
                        ) ?? t('labels.order.serials.pending')}
                      </code>
                    }
                  />
                  <DetailRow label={t('labels.order.field.keyVersion')} value={data.key_version} />
                  <DetailRow
                    label={t('labels.orders.column.generated')}
                    value={
                      data.generated_at === null || data.generated_at === undefined
                        ? '—'
                        : fmt.dateTime(data.generated_at)
                    }
                  />
                  <DetailRow
                    label={t('labels.order.field.notes')}
                    value={data.notes ?? '—'}
                  />
                  <DetailRow
                    label={t('labels.orders.column.created')}
                    value={fmt.dateTime(data.created_at)}
                  />
                </DetailList>
              </CardBody>
            </Card>

            {opera && (
              <Card>
                <CardHeader
                  title={t('labels.order.generate.section')}
                  description={t('labels.order.generate.section.hint')}
                />
                <CardBody>
                  <GenerationPanel order={data} onGeneratingChange={onGeneratingChange} />
                </CardBody>
              </Card>
            )}

            {opera && (
              <Card>
                <CardHeader
                  title={t('labels.printFile.title')}
                  description={t('labels.printFile.description')}
                />
                <CardBody>
                  {data.artifact_key === null || data.artifact_key === undefined ? (
                    <EmptyState
                      title={t('labels.printFile.notGenerated')}
                      detail={t('labels.printFile.notGenerated.detail')}
                    />
                  ) : (
                    <>
                      <PrintFilePanel orderId={orderId} />
                      {/* La verificación va DEBAJO de la entrega y no arriba:
                          el orden en pantalla es el orden del trabajo real —
                          se genera, se mira que esté bien, y recién ahí se
                          manda. */}
                      <div className="mt-6 border-t border-border pt-4">
                        <VerifyPanel orderId={orderId} />
                      </div>
                      {/* La clave va en su propio bloque y al final: no puede
                          quedar al lado del enlace de descarga. Las dos mitades
                          juntas en la pantalla es cómo terminan juntas en el
                          mismo correo, y ahí la separación de canales deja de
                          existir. Sólo XYVONA llega hasta acá (`opera`), y la
                          API exige además el permiso `print_file:reveal_key`. */}
                      <div className="mt-6 border-t border-border pt-4">
                        <OrderKeyPanel orderId={orderId} />
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            )}

            <Card>
              <CardHeader
                title={t('labels.order.rolls.title')}
                description={t('labels.order.rolls.description')}
              />

              {rolls.query.isPending && <LoadingState rows={4} />}
              {rolls.query.isError && (
                <CardBody>
                  <ErrorState
                    error={rolls.query.error}
                    onRetry={() => {
                      void rolls.query.refetch();
                    }}
                  />
                </CardBody>
              )}
              {!rolls.query.isPending && !rolls.query.isError && rolls.items.length === 0 && (
                <EmptyState title={t('labels.order.rolls.empty')} />
              )}

              {rolls.items.length > 0 && (
                <>
                  <Table caption={t('labels.order.rolls.description')}>
                    <thead>
                      <tr>
                        <Th>{t('labels.rolls.column.code')}</Th>
                        <Th>{t('labels.rolls.column.serials')}</Th>
                        <Th className="text-right">{t('labels.rolls.column.units')}</Th>
                        <Th>{t('labels.rolls.column.state')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {rolls.items.map((roll) => (
                        <tr key={roll.id}>
                          <Td>
                            <Link
                              to={`/labels/rolls/${String(roll.id)}`}
                              className="font-mono font-medium text-accent underline underline-offset-2"
                            >
                              {roll.roll_code ?? `#${String(roll.id)}`}
                            </Link>
                          </Td>
                          <Td className="font-mono text-xs text-ink-muted">
                            {serialRange(roll.serial_from, roll.serial_to, (value) =>
                              fmt.number(value),
                            ) ?? '—'}
                          </Td>
                          <Td className="text-right tabular-nums">{fmt.number(roll.unit_count)}</Td>
                          <Td>
                            <Badge
                              tone={isRollState(roll.state) ? ROLL_STATE_TONES[roll.state] : 'neutral'}
                            >
                              {isRollState(roll.state)
                                ? t(ROLL_STATE_LABEL_KEYS[roll.state])
                                : roll.state}
                            </Badge>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <CardBody className="flex items-center justify-center border-t border-border">
                    {rolls.hasMore ? (
                      <Button size="sm" onClick={rolls.loadMore} disabled={rolls.isLoadingMore}>
                        {rolls.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                      </Button>
                    ) : (
                      <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
                    )}
                  </CardBody>
                </>
              )}
            </Card>

            <Dialog
              open={cancelling}
              onClose={() => {
                setCancelling(false);
              }}
              tone="critical"
              title={t('labels.order.cancel.title')}
              description={t('labels.order.cancel.description')}
              footer={
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setCancelling(false);
                    }}
                    disabled={cancel.isPending}
                  >
                    {t('form.cancel')}
                  </Button>
                  <Button
                    variant="critical"
                    onClick={() => {
                      cancel.mutate();
                    }}
                    disabled={cancel.isPending}
                  >
                    {cancel.isPending ? t('form.saving') : t('labels.order.cancel.confirm')}
                  </Button>
                </>
              }
            >
              <p className="text-sm text-ink">
                {t('labels.order.cancel.detail', { code: data.order_code })}
              </p>
              {Boolean(cancel.error) && (
                <div className="mt-3">
                  <ErrorState error={cancel.error} />
                </div>
              )}
            </Dialog>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}
